import {
	BigNum,
	BN,
	PRICE_PRECISION,
	PRICE_PRECISION_EXP,
	PublicKey,
	SpotMarketAccount,
	StrictOraclePrice,
	SwapMode,
	User,
	ZERO,
	SpotMarketConfig,
	UnifiedSwapClient,
	getSpotAssetValue,
	getSpotLiabilityValue,
	VelocityClient,
	QUOTE_PRECISION_EXP,
	calculateSizeDiscountAssetWeight,
	calculateSizePremiumLiabilityWeight,
	SPOT_MARKET_WEIGHT_PRECISION,
	calculateEstimatedSuperStakeLiquidationPrice,
	convertToNumber,
} from '@velocity-exchange/sdk';
import { AccountData } from 'src/stores/useVelocityAccountsStore';
import { JLP_POOL_ID, MarketId } from '@velocity-exchange/common';
import { SlippageTolerance } from 'src/@types/types';
import { AmplifyStats } from 'src/hooks/useAmplifyStats';
import { getCloseBorrowAmountWithBuffer } from './borrowLend';
import { getSimulatedQuoteValues } from 'src/utils/swap';
import { getCleanedMarketSymbol } from 'src/utils/market';
import { MarginInfo } from 'src/stores/velocityStoreTypes';
import { parseInputBigNumOrZero } from 'src/utils/parseInputBigNum';

const DEFAULT_HEALTH_COMPONENTS = {
	health: 100,
	liqPrice: BigNum.zero(PRICE_PRECISION_EXP),
	priceChangeToLiq: -100,
};

export const getProjectedHealthComponents = ({
	previewDepositAmount,
	previewBorrowAmount,
	depositAssetOraclePrice,
	borrowAssetOraclePrice,
	depositAssetSpotMarket,
	borrowAssetSpotMarket,
	user,
	isMaxWithdraw,
	velocityClient,
}: {
	previewDepositAmount: BigNum;
	previewBorrowAmount: BigNum;
	depositAssetOraclePrice: BigNum;
	borrowAssetOraclePrice: BigNum;
	depositAssetSpotMarket: SpotMarketAccount;
	borrowAssetSpotMarket: SpotMarketAccount;
	user: User;
	isMaxWithdraw?: boolean;
	velocityClient: VelocityClient;
}): { health: number; liqPrice: BigNum; priceChangeToLiq: number } => {
	try {
		if (isMaxWithdraw) {
			return DEFAULT_HEALTH_COMPONENTS;
		}

		const depositAssetValue = BigNum.from(
			getSpotAssetValue(
				previewDepositAmount.val,
				new StrictOraclePrice(depositAssetOraclePrice.val),
				depositAssetSpotMarket,
				user?.getUserAccount()?.maxMarginRatio ?? 0,
				'Maintenance'
			),
			QUOTE_PRECISION_EXP
		);

		const borrowLiabilityValue = BigNum.from(
			getSpotLiabilityValue(
				previewBorrowAmount.val,
				new StrictOraclePrice(borrowAssetOraclePrice.val),
				borrowAssetSpotMarket,
				user?.getUserAccount()?.maxMarginRatio ?? 0,
				'Maintenance',
				new BN(velocityClient.getStateAccount().liquidationMarginBufferRatio)
			),
			QUOTE_PRECISION_EXP
		);

		const maintenanceWeight = calculateSizeDiscountAssetWeight(
			previewDepositAmount.val,
			new BN(depositAssetSpotMarket.imfFactor),
			new BN(depositAssetSpotMarket.maintenanceAssetWeight)
		);

		const liabilityWeight = calculateSizePremiumLiabilityWeight(
			previewBorrowAmount.val,
			new BN(borrowAssetSpotMarket.imfFactor),
			new BN(borrowAssetSpotMarket.maintenanceLiabilityWeight),
			SPOT_MARKET_WEIGHT_PRECISION
		);

		const liqPrice = calculateEstimatedSuperStakeLiquidationPrice(
			previewDepositAmount.toNum(),
			convertToNumber(maintenanceWeight, SPOT_MARKET_WEIGHT_PRECISION),
			previewBorrowAmount.toNum(),
			convertToNumber(liabilityWeight, SPOT_MARKET_WEIGHT_PRECISION),
			depositAssetOraclePrice.toNum() // to do - this is really a RATIO but since denominator is USDC in this case just use the JLP price
		);

		const health = Math.round(
			Math.min(
				100,
				Math.max(
					0,
					(1 - borrowLiabilityValue.toNum() / depositAssetValue.toNum()) * 100
				)
			)
		);

		const pctChangeToLiq =
			((depositAssetOraclePrice.toNum() - liqPrice) /
				depositAssetOraclePrice.toNum()) *
			100;

		return {
			health: isNaN(health) ? 100 : health,
			liqPrice: BigNum.fromPrint(`${liqPrice}`, PRICE_PRECISION_EXP),
			priceChangeToLiq: pctChangeToLiq * -1,
		};
	} catch {
		// hitting this sometimes, have to investigate more why it happens
		return DEFAULT_HEALTH_COMPONENTS;
	}
};

export const getExistingHealthComponents = ({
	user,
	depositAssetMarketIndex,
	depositAssetOraclePrice,
}: {
	user: User;
	depositAssetMarketIndex: number;
	depositAssetOraclePrice: BigNum;
}): { health: number; liqPrice: BigNum; priceChangeToLiq: number } => {
	const health = user.getHealth();

	const liqPriceBn = BN.max(
		user.spotLiquidationPrice(depositAssetMarketIndex),
		ZERO
	);
	const liqPrice = BigNum.from(liqPriceBn, PRICE_PRECISION_EXP);

	const pctChangeToLiq =
		((depositAssetOraclePrice.toNum() - liqPrice.toNum()) /
			depositAssetOraclePrice.toNum()) *
		100;

	return {
		health,
		liqPrice,
		priceChangeToLiq: pctChangeToLiq * -1,
	};
};

export const getProjectedEarnings = ({
	previewDepositAmount,
	previewBorrowAmount,
	depositAssetOraclePrice,
	borrowAssetOraclePrice,
	days,
	depositApy,
	borrowApy,
}: {
	previewDepositAmount: BigNum;
	previewBorrowAmount: BigNum;
	depositAssetOraclePrice: BigNum;
	borrowAssetOraclePrice: BigNum;
	days: number;
	depositApy: number;
	borrowApy: number;
}) => {
	const timeInYears = days / 365;

	// Calculate total JLP amount after interest using deposit APY
	const totalJlpAmount = previewDepositAmount.scale(
		Math.floor((1 + depositApy / 100) ** timeInYears * 10000),
		10000
	);

	// Calculate earnings in JLP
	const jlpEarnings = totalJlpAmount.sub(previewDepositAmount);

	// Convert JLP earnings to dollar value using oracle price
	const dollarEarnings = jlpEarnings.mul(depositAssetOraclePrice);

	// Calculate borrow costs using borrow APY
	const borrowCost = previewBorrowAmount
		.mul(borrowAssetOraclePrice)
		.scale(Math.floor((1 + borrowApy / 100) ** timeInYears * 10000), 10000)
		.sub(previewBorrowAmount.mul(borrowAssetOraclePrice));

	// Return net dollar earnings
	return dollarEarnings.sub(borrowCost);
};

export const calculateBorrowAmountToRepayForWithdrawal = ({
	netQuoteAccountValue,
	withdrawAmount,
	currentBorrowedAmount,
	withdrawalAssetOraclePrice,
	withdrawalAssetIsSameAsDeposit,
}: {
	netQuoteAccountValue: BN;
	withdrawAmount: BigNum;
	currentBorrowedAmount: BigNum;
	withdrawalAssetOraclePrice: BigNum;
	withdrawalAssetIsSameAsDeposit: boolean;
}) => {
	const withdrawalRequestQuoteAmount = withdrawalAssetOraclePrice.val
		.mul(withdrawAmount.val)
		.div(PRICE_PRECISION);

	const portionOfBorrowToRepay =
		withdrawalRequestQuoteAmount.toNumber() / netQuoteAccountValue.toNumber();

	let borrowAmountToRepay = currentBorrowedAmount.scale(
		Math.round(portionOfBorrowToRepay * 10_000),
		10_000
	);

	if (!withdrawalAssetIsSameAsDeposit) {
		borrowAmountToRepay = borrowAmountToRepay.add(withdrawAmount);
	}

	return borrowAmountToRepay;
};

export const getAmplifyAccountName = ({
	depositMarketSymbol,
	borrowMarketSymbol,
}: {
	depositMarketSymbol: string;
	borrowMarketSymbol: string;
}) => {
	return `Amplify ${getCleanedMarketSymbol(
		depositMarketSymbol
	)}/${getCleanedMarketSymbol(borrowMarketSymbol)}`;
};

export const isAmplifyAccountName = (accountName: string): boolean => {
	const amplifyPattern = /^Amplify\s+([^/]+)\/([^/]+)$/;
	return amplifyPattern.test(accountName);
};

export const userHasAmplifyAccount = ({
	subAccounts,
	depositMarketSymbol,
	borrowMarketSymbol,
}: {
	subAccounts: AccountData[];
	depositMarketSymbol: string | undefined | null;
	borrowMarketSymbol: string | undefined | null;
}) => {
	// Return false if either symbol is undefined or null
	if (!depositMarketSymbol || !borrowMarketSymbol) {
		return false;
	}

	// Create patterns for both cleaned and uncleaned symbols
	const cleanedPattern = `Amplify ${depositMarketSymbol.replace(
		/-[0-9]+$/,
		''
	)}/${borrowMarketSymbol.replace(/-[0-9]+$/, '')}`;
	const uncleanedPattern = `Amplify ${depositMarketSymbol}/${borrowMarketSymbol}`;

	// Check if any subaccount name contains either pattern
	const result = subAccounts.find((subAccount) => {
		if (subAccount.poolId !== JLP_POOL_ID) {
			return false;
		}

		// Check for exact match with either pattern
		const exactMatch =
			subAccount.name === cleanedPattern ||
			subAccount.name === uncleanedPattern;

		// Check for partial match with either pattern
		const partialMatch =
			subAccount.name.includes(cleanedPattern) ||
			subAccount.name.includes(uncleanedPattern);

		// Special case for "Amplify JLP-1/USDC-1" style names
		const specialCaseMatch =
			subAccount.name.startsWith('Amplify ') &&
			subAccount.name.includes(depositMarketSymbol) &&
			subAccount.name.includes(borrowMarketSymbol);

		const matches = exactMatch || partialMatch || specialCaseMatch;
		return matches;
	});

	return result;
};

// todo - write tests for this
export const getQuoteParamsForWithdrawal = ({
	withdrawAmount,
	depositAssetOraclePrice,
	borrowAssetOraclePrice,
	assetToWithdraw,
	isMaxWithdraw,
	slippage,
	userMarginInfo,
	amplifyStats,
	velocityClient,
}: {
	withdrawAmount: BigNum;
	depositAssetOraclePrice: BigNum;
	borrowAssetOraclePrice: BigNum;
	assetToWithdraw: SpotMarketConfig;
	isMaxWithdraw: boolean;
	slippage: SlippageTolerance;
	userMarginInfo: MarginInfo;
	amplifyStats: AmplifyStats;
	velocityClient: VelocityClient;
}): {
	swapAmount: BigNum;
	swapType: SwapMode;
	slippageParams: {
		autoSlippage: boolean;
		slippageBps?: number;
		maxAutoSlippageBps?: number;
		usdEstimate?: number;
	};
} => {
	if (!amplifyStats.borrowMarketConfig || !amplifyStats.depositMarketConfig) {
		throw new Error('Market configs are required for withdrawal');
	}

	let swapAmount: BigNum;
	let swapType: SwapMode;
	let usdEstimate: number;

	const withdrawalAssetIsSameAsDeposit =
		assetToWithdraw.marketIndex ===
		amplifyStats.depositMarketConfig.marketIndex;

	const borrowAmount = amplifyStats.userBorrowsBase;

	const depositAmount = amplifyStats.userDepositsBase;

	if (withdrawalAssetIsSameAsDeposit) {
		// if withdrawing the max in deposit asset, repay the full borrow and withdraw whatevers left
		if (isMaxWithdraw) {
			// this function is inexact but use to not leave a dust borrow.
			// worst case is a dust deposit is leftover rather than a dust deposit and a dust borrow.
			// add an aditional one cent to make sure we overshoot
			swapAmount = getCloseBorrowAmountWithBuffer(
				velocityClient,
				amplifyStats.borrowMarketConfig.marketIndex,
				borrowAmount
			).add(
				BigNum.fromPrint('.01', amplifyStats.borrowMarketConfig.precisionExp)
			);
			swapType = 'ExactOut';
			usdEstimate = borrowAmount.toNum() * borrowAssetOraclePrice.toNum();
		} else {
			swapAmount = calculateBorrowAmountToRepayForWithdrawal({
				netQuoteAccountValue: userMarginInfo.netUsdValue,
				withdrawAmount,
				currentBorrowedAmount: borrowAmount,
				withdrawalAssetOraclePrice: depositAssetOraclePrice,
				withdrawalAssetIsSameAsDeposit,
			});
			swapType = 'ExactOut';
			usdEstimate = swapAmount.toNum() * borrowAssetOraclePrice.toNum();
		}
	} else {
		// if withdrawing the max in borrow asset, swap our full deposit asset balance for the borrow asset and withdraw that
		if (isMaxWithdraw) {
			swapAmount = depositAmount;
			swapType = 'ExactIn';
			usdEstimate = depositAmount.toNum() * depositAssetOraclePrice.toNum();
		} else {
			swapAmount = calculateBorrowAmountToRepayForWithdrawal({
				netQuoteAccountValue: userMarginInfo.netUsdValue,
				withdrawAmount,
				currentBorrowedAmount: borrowAmount,
				withdrawalAssetOraclePrice: borrowAssetOraclePrice,
				withdrawalAssetIsSameAsDeposit,
			});
			swapType = 'ExactOut';
			usdEstimate = swapAmount.toNum() * borrowAssetOraclePrice.toNum();
		}
	}

	const slippageParams =
		slippage === 'dynamic'
			? {
					autoSlippage: true,
					maxAutoSlippageBps: 300, // 3 pct
					usdEstimate: Math.round(usdEstimate),
				}
			: {
					slippageBps: (slippage as number) * 100,
					autoSlippage: false,
				};

	return {
		swapAmount,
		swapType,
		slippageParams,
	};
};

// todo - write tests for this
export const calculateDepositBorrowAmounts = async ({
	isDeposit,
	inputAmount,
	leverage,
	userHasPosition,
	inputAsset,
	netQuoteAccountValue,
	amplifyMarketStats,
	jupiterClient,
	getOraclePrice,
	useEstimateQuotes,
	authority,
}: {
	isDeposit: boolean;
	inputAmount: string;
	leverage: number;
	userHasPosition: boolean;
	inputAsset: SpotMarketConfig;
	netQuoteAccountValue: BN;
	amplifyMarketStats: AmplifyStats;
	jupiterClient: UnifiedSwapClient;
	getOraclePrice: (marketId: any) => BigNum;
	useEstimateQuotes?: boolean;
	authority: PublicKey;
}): Promise<{
	previewDepositAmount: BigNum;
	previewBorrowAmount: BigNum;
	hasQuoteError?: boolean;
	priceImpactPct?: string;
}> => {
	if (
		!amplifyMarketStats.borrowMarketConfig ||
		!amplifyMarketStats.depositMarketConfig
	) {
		throw new Error(
			'Market configs are required for deposit/borrow calculation'
		);
	}

	const inputAssetIsDepositAsset =
		inputAsset.marketIndex ===
		amplifyMarketStats.depositMarketConfig.marketIndex;
	const leverageToUse = inputAssetIsDepositAsset ? leverage - 1 : leverage;

	try {
		if (isDeposit) {
			const depositAmount = parseInputBigNumOrZero(
				inputAmount,
				inputAsset.precisionExp
			);

			const swapAmount = depositAmount.scale(leverageToUse * 10_000, 10_000);

			// shouldnt get here, but dont send invalid requests if we somehow do
			if (swapAmount.isNeg() || swapAmount.eqZero()) {
				return {
					previewBorrowAmount: BigNum.zero(
						amplifyMarketStats.borrowMarketConfig.precisionExp
					),
					previewDepositAmount: BigNum.zero(
						amplifyMarketStats.depositMarketConfig.precisionExp
					),
					hasQuoteError: false,
				};
			}

			let swapInAmount: BigNum;
			let swapOutAmount: BigNum;
			let priceImpactPct: string | undefined;

			// use oracle prices to estimate a quote so we don't get rate limited by jupiter
			if (!useEstimateQuotes) {
				const quote = await jupiterClient.getQuote({
					inputMint: amplifyMarketStats.borrowMarketConfig.mint,
					outputMint: amplifyMarketStats.depositMarketConfig.mint,
					amount: swapAmount.val,
					userPublicKey: authority,
					swapMode: inputAssetIsDepositAsset ? 'ExactOut' : 'ExactIn',
					...(jupiterClient.isTitan() ? {} : { maxAccounts: 15 }),
				});

				if (quote.error) {
					console.error('Error in jupiter quote response', quote);
					return {
						previewBorrowAmount: BigNum.zero(
							amplifyMarketStats.borrowMarketConfig.precisionExp
						),
						previewDepositAmount: BigNum.zero(
							amplifyMarketStats.depositMarketConfig.precisionExp
						),
						hasQuoteError: true,
					};
				}

				swapOutAmount = BigNum.from(
					new BN(quote.outAmount),
					amplifyMarketStats.depositMarketConfig.precisionExp
				);

				swapInAmount = BigNum.from(
					new BN(quote.inAmount),
					amplifyMarketStats.borrowMarketConfig.precisionExp
				);

				priceImpactPct = quote.priceImpactPct;
			} else {
				const outAssetOraclePrice = getOraclePrice(
					MarketId.createSpotMarket(
						amplifyMarketStats.depositMarketConfig.marketIndex
					)
				);

				const inAssetOraclePrice = getOraclePrice(
					MarketId.createSpotMarket(
						amplifyMarketStats.borrowMarketConfig.marketIndex
					)
				);

				const {
					swapInAmount: simulatedSwapInAmount,
					swapOutAmount: simulatedSwapOutAmount,
				} = getSimulatedQuoteValues(
					swapAmount,
					inputAssetIsDepositAsset ? 'ExactOut' : 'ExactIn',
					inAssetOraclePrice,
					outAssetOraclePrice
				);

				swapOutAmount = simulatedSwapOutAmount;
				swapInAmount = simulatedSwapInAmount;
			}

			if (userHasPosition) {
				return {
					previewDepositAmount: swapOutAmount
						.add(amplifyMarketStats.userDepositsBase)
						.add(
							inputAssetIsDepositAsset
								? depositAmount
								: BigNum.zero(
										amplifyMarketStats.depositMarketConfig.precisionExp
									)
						),
					previewBorrowAmount: amplifyMarketStats.userBorrowsBase
						.add(swapInAmount)
						.sub(
							inputAssetIsDepositAsset
								? BigNum.zero(
										amplifyMarketStats.borrowMarketConfig.precisionExp
									)
								: depositAmount
						),
					hasQuoteError: false,
					priceImpactPct,
				};
			} else {
				return {
					previewDepositAmount: swapOutAmount.add(
						inputAssetIsDepositAsset
							? depositAmount
							: BigNum.zero(amplifyMarketStats.depositMarketConfig.precisionExp)
					),
					previewBorrowAmount: swapInAmount.sub(
						inputAssetIsDepositAsset
							? BigNum.zero(amplifyMarketStats.borrowMarketConfig.precisionExp)
							: depositAmount
					),
					hasQuoteError: false,
					priceImpactPct,
				};
			}
		} else {
			// nothing to withdraw if no position
			if (!userHasPosition) {
				return {
					previewBorrowAmount: BigNum.zero(
						amplifyMarketStats.borrowMarketConfig.precisionExp
					),
					previewDepositAmount: BigNum.zero(
						amplifyMarketStats.depositMarketConfig.precisionExp
					),
					hasQuoteError: false,
				};
			}

			const borrowAmountToRepay = calculateBorrowAmountToRepayForWithdrawal({
				netQuoteAccountValue,
				withdrawAmount: parseInputBigNumOrZero(
					inputAmount,
					inputAsset.precisionExp
				),
				currentBorrowedAmount: amplifyMarketStats.userBorrowsBase,
				withdrawalAssetOraclePrice: getOraclePrice(
					MarketId.createSpotMarket(inputAsset.marketIndex)
				),
				withdrawalAssetIsSameAsDeposit: inputAssetIsDepositAsset,
			});

			const quote = await jupiterClient.getQuote({
				inputMint: amplifyMarketStats.depositMarketConfig.mint,
				outputMint: amplifyMarketStats.borrowMarketConfig.mint,
				amount: borrowAmountToRepay.val,
				userPublicKey: authority,
				swapMode: 'ExactOut',
				...(jupiterClient.isTitan() ? {} : { maxAccounts: 15 }),
			});

			const swapOutAmount = BigNum.from(
				new BN(quote.outAmount),
				amplifyMarketStats.borrowMarketConfig.precisionExp
			);

			const swapInAmount = BigNum.from(
				new BN(quote.inAmount),
				amplifyMarketStats.depositMarketConfig.precisionExp
			);

			return {
				previewDepositAmount: BigNum.max(
					amplifyMarketStats.userDepositsBase
						.sub(swapInAmount)
						.sub(
							inputAssetIsDepositAsset
								? parseInputBigNumOrZero(
										inputAmount,
										amplifyMarketStats.depositMarketConfig.precisionExp
									)
								: BigNum.zero(
										amplifyMarketStats.depositMarketConfig.precisionExp
									)
						),
					BigNum.zero(amplifyMarketStats.depositMarketConfig.precisionExp)
				),
				previewBorrowAmount: amplifyMarketStats.userBorrowsBase
					.sub(swapOutAmount)
					.add(
						!inputAssetIsDepositAsset
							? parseInputBigNumOrZero(
									inputAmount,
									amplifyMarketStats.borrowMarketConfig.precisionExp
								)
							: BigNum.zero(amplifyMarketStats.borrowMarketConfig.precisionExp)
					),
				hasQuoteError: false,
				priceImpactPct: quote.priceImpactPct,
			};
		}
	} catch (error) {
		console.error('Error calculating borrow amount:', error);
		return {
			previewBorrowAmount: BigNum.zero(
				amplifyMarketStats.borrowMarketConfig.precisionExp
			),
			previewDepositAmount: BigNum.zero(
				amplifyMarketStats.depositMarketConfig.precisionExp
			),
			hasQuoteError: true,
		};
	}
};
